import React, {useEffect, useState, useRef, useMemo} from 'react';
import {LayoutSelector, PagesSelector} from '../../../Selectors';
import {useSelector} from 'react-redux';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {
  CoreDoms,
  LayoutContext,
  ViewMode,
  Viewers,
  EditorExperienceType,
} from '@taopix/taopix-core';
import classNames from 'classnames';
import {GridSizes} from '../../GridSizeSelector/GridSizeSelector';
import {AssistantContent} from '../AssistantContent';
import {AssistantHeader} from '../AssistantHeader';
import {AssistantFooter} from '../AssistantFooter';
import {AssistantNav} from '../AssistantNav';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import PreviewGrid from '../Common/PreviewGrid';
import {useAssistant} from '../../Context/Assistant/AssistantContext';
import {useTaopix} from '../../Context/TaopixContext';
import {useUserFlowState} from '../../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import useSettings from '../../../Hooks/useSettings';
import {CheckSmallIcon, DisplayMode, LoadingOverlay} from '@taopix/taopix-design-system';
import {useTranslation} from 'react-i18next';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {AssistantCommonProps, AssistantHeaderProps, AssistantNavigationProps} from '../Assistant';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {useExperience} from '../../Experience/ExperienceProvider';
import AssistantStepTip from '../Common/AssistantStepTip/AssistantStepTip';
import {DialogStatus} from '../../../Enums';
import PreviewList from '../Common/PreviewList';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import Preview3d from '../Common/Preview3d';

type PreviewStepProps = {} & AssistantCommonProps & AssistantNavigationProps & AssistantHeaderProps;

export const PreviewStep = ({
  onContinueInEditor,
  onClose,
  backButton,
  nextButton,
  displayMode,
}: PreviewStepProps) => {
  const taopix = useTaopix();
  const ui = useUiState();
  const assistant = useAssistant();
  const userFlow = useUserFlowState();
  const settings = useSettings();
  const {experience} = useExperience();
  const {t} = useTranslation(['main']);

  const pages = useSelector(PagesSelector);
  const productLayout = useSelector(LayoutSelector);

  const [helpTipOpen, setHelpTipOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const helpTipTimeout = useRef<number>(0);

  const stepActive = userFlow.isStepActive(UserFlowType.Assistant, UserFlowStep.AssistantPreview);
  const isThreeDee =
    productLayout.hasOwnProperty('threeDDetails') && '' !== productLayout.threeDDetails?.fileUrl;

  useEffect(() => {
    if (!stepActive) return;
    if (DialogStatus.Open === ui.state.dialogs.picturesIgnoredBySmartDesignNotification) {
      window.clearTimeout(helpTipTimeout.current);
      return;
    }

    helpTipTimeout.current = window.setTimeout(() => {
      setHelpTipOpen(true);
    }, 500);
  }, [stepActive, ui.state.dialogs.picturesIgnoredBySmartDesignNotification]);

  useEffect(() => {
    if (stepActive) {
      // Must reload the project even if in the Designer in case the page count changed.
      taopix.core
        .get('loader')
        .loadProject(settings.state.tokens.session)
        .then(() => {
          setLoading(false);
        });
    }
  }, [stepActive]);

  const tpxCoreAttrs = {
    [CoreDoms.ProjectViewerAttr]: Viewers.Tertiary,
    [CoreDoms.MaxWidthAttr]: '100',
    [CoreDoms.MaxHeightAttr]: '60',
    [CoreDoms.ResizeThresholdAttr]: '10',
    [CoreDoms.InsetWidthAttr]: '100',
  };

  // Classes for the UL
  const pagesContainerClasses = classNames('flex-1', 'overflow-y-auto', 'pt-lg', 'self-start');

  const getNextButton = (): React.ReactElement => {
    if (
      DisplayMode.Fullscreen === displayMode &&
      (experience.WIZARD.previewStep.editButton || experience.WIZARD.previewStep.orderButton)
    ) {
      // Don't show next button if edit or order buttons are enabled.
      // Return empty span so the <AssistantNav> gets rendered correctly.
      return (
        <>
          <span></span>
        </>
      );
    }

    // Show close button when in dialog mode.
    const overrides: Partial<ButtonProps> = {};
    if (DisplayMode.Positioned === displayMode) {
      overrides.label = t('*:str_ButtonDone');
      overrides.endIcon = ui.state.isSmallScreen ? <CheckSmallIcon /> : null;
      overrides.hideLabel = ui.state.isSmallScreen;
    }

    return React.cloneElement(nextButton, {
      ...nextButton.props,
      ...overrides,
    });
  };

  const viewModeRef = useRef<ViewMode>();

  useEffect(() => {
    if (ui.state.isSmallScreen) {
      ui.dispatch(UserInterfaceActions.setViewMode(ViewMode.List));
      viewModeRef.current = ViewMode.List;
    } else {
      viewModeRef.current = ViewMode.Grid;
      ui.dispatch(UserInterfaceActions.setViewMode(ViewMode.Grid));
    }
  }, [ui.state.isSmallScreen]);

  const gridGap = ui.state.isSmallScreen ? 15 : 20;

  const helpTip = (
    <AssistantStepTip
      title={t('*:str_TitlePreview')}
      message={t('assistants:str_MessageReviewBeforeOrdering')}
      open={helpTipOpen}
      onClose={() => setHelpTipOpen(false)}
      key={'assistant-preview-helptip'}
      anchorId={'assistant-content'}
      nextFocusContainerId={'assistant-content'}
    />
  );

  const previewCommonProps = {
    layout: productLayout,
    editorType: EditorExperienceType.ReadOnly,
    gridSize: GridSizes.LARGE,
    gridRowGap: gridGap,
    gridColGap: gridGap,
    showErrors: false,
    showEmptyBoxes: false,
    showToolbar: false,
    jumpToPage: 0,
  };

  const content = useMemo(() => {
    if (isThreeDee && !loading) {
      return <Preview3d />;
    }

    if (viewModeRef.current === ViewMode.Grid) {
      return (
        <div {...tpxCoreAttrs} className={'flex relative h-full w-full'}>
          <ul data-project-pages="true" data-render-mode="grid" className={pagesContainerClasses}>
            {pages.map((page: PageInterface) => {
              return (
                <PreviewGrid
                  {...previewCommonProps}
                  key={`${assistant.state.runCount}-${page.ref}`}
                  page={page}
                  onContinueInEditor={onContinueInEditor}
                />
              );
            })}
          </ul>
        </div>
      );
    }

    return (
      <div {...tpxCoreAttrs} className={'flex relative h-full w-full'}>
        <ul data-project-pages="true" data-render-mode="grid" className={pagesContainerClasses}>
          {pages.map((page: PageInterface) => {
            return (
              <PreviewList
                {...previewCommonProps}
                key={`${assistant.state.runCount}-${page.ref}`}
                page={page}
                onContinueInEditor={onContinueInEditor}
              />
            );
          })}
        </ul>
      </div>
    );
  }, [viewModeRef.current, isThreeDee, assistant.state.runCount, loading]);

  return (
    <>
      <AssistantHeader displayMode={displayMode} onContinueInEditor={onContinueInEditor} />
      <AssistantNav
        onClose={onClose}
        backButton={backButton}
        nextButton={getNextButton()}
        onNext={() => {}}
      />
      <AssistantContent>
        {helpTip}
        {!loading && content}
      </AssistantContent>
      <AssistantFooter />
      <LoadingOverlay
        id={'assistant-preview'}
        open={loading}
        label={t('str_TitleLoadingProject')}
        delay={0}
      />
    </>
  );
};
